import React from 'react';
import SkeletonLoader from './SkeletonLoader';
import ErrorDisplay, { NetworkError, ValidationError, AuthError, ServerError } from './ErrorDisplay';
import EnhancedProgressBar from './EnhancedProgressBar';
import useConnectivity from '../../hooks/useConnectivity';

// Composant d'indicateur de connectivité
interface ConnectivityIndicatorProps {
  className?: string;
  showDetails?: boolean;
}

export const ConnectivityIndicator: React.FC<ConnectivityIndicatorProps> = ({
  className = '',
  showDetails = false
}) => {
  const { isOnline, connectionQuality, queueStatus, getTimeSinceLastOnline } = useConnectivity();

  const getIndicatorColor = () => {
    if (!isOnline) return 'bg-red-500';
    switch (connectionQuality) {
      case 'excellent': return 'bg-green-500';
      case 'good': return 'bg-blue-500';
      case 'poor': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = () => {
    if (!isOnline) return getTimeSinceLastOnline();
    switch (connectionQuality) {
      case 'excellent': return 'Connexion excellente';
      case 'good': return 'Connexion stable';
      case 'poor': return 'Connexion lente';
      default: return 'En ligne';
    }
  };

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className={`w-2 h-2 rounded-full ${getIndicatorColor()}`} />
      {showDetails && (
        <div className="text-xs text-gray-600">
          <span>{getStatusText()}</span>
          {queueStatus.total > 0 && (
            <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 rounded-full">
              {queueStatus.total} en attente
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Composant de notification toast
interface ToastNotificationProps {
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  onClose: () => void;
  autoClose?: boolean;
  duration?: number;
}

export const ToastNotification: React.FC<ToastNotificationProps> = ({
  type,
  title,
  message,
  onClose,
  autoClose = true,
  duration = 5000
}) => {
  React.useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [autoClose, duration, onClose]);

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  return (
    <div className={`fixed top-4 right-4 max-w-sm w-full border rounded-lg shadow-lg p-4 z-50 ${getTypeStyles()} animate-slide-in-right`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        <div className="ml-3 flex-1">
          <h4 className="text-sm font-medium">{title}</h4>
          <p className="text-sm mt-1 opacity-90">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Hook pour gérer les notifications toast
export function useToast() {
  const [toasts, setToasts] = React.useState<Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
  }>>([]);

  const addToast = React.useCallback((
    type: 'success' | 'error' | 'warning' | 'info',
    title: string,
    message: string
  ) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { id, type, title, message }]);
  }, []);

  const removeToast = React.useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showSuccess = React.useCallback((title: string, message: string) => {
    addToast('success', title, message);
  }, [addToast]);

  const showError = React.useCallback((title: string, message: string) => {
    addToast('error', title, message);
  }, [addToast]);

  const showWarning = React.useCallback((title: string, message: string) => {
    addToast('warning', title, message);
  }, [addToast]);

  const showInfo = React.useCallback((title: string, message: string) => {
    addToast('info', title, message);
  }, [addToast]);

  const ToastContainer = React.useCallback(() => (
    <div className="fixed top-4 right-4 space-y-2 z-50">
      {toasts.map(toast => (
        <ToastNotification
          key={toast.id}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  ), [toasts, removeToast]);

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    ToastContainer
  };
}

// Composant principal du système de feedback
interface FeedbackSystemProps {
  children: React.ReactNode;
  showConnectivityIndicator?: boolean;
}

export const FeedbackSystem: React.FC<FeedbackSystemProps> = ({
  children,
  showConnectivityIndicator = true
}) => {
  const { isOffline, queueStatus } = useConnectivity();

  return (
    <div className="relative">
      {children}
      
      {/* Indicateur de connectivité */}
      {showConnectivityIndicator && (
        <div className="fixed bottom-4 left-4 z-40">
          <ConnectivityIndicator showDetails={isOffline || queueStatus.total > 0} />
        </div>
      )}

      {/* Banner hors ligne */}
      {isOffline && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-500 text-white text-center py-2 z-50">
          <span className="text-sm font-medium">
            Mode hors ligne - Vos actions seront synchronisées à la reconnexion
          </span>
        </div>
      )}
    </div>
  );
};

// Exports pour faciliter l'utilisation
export {
  SkeletonLoader,
  ErrorDisplay,
  NetworkError,
  ValidationError,
  AuthError,
  ServerError,
  EnhancedProgressBar
};

export default FeedbackSystem;
